import axios from 'axios';
import { baseUrl, accountUrl } from '../config/constants';

export default {
  getAccounts: async () => {
    const res = await axios.get(`${baseUrl}/accounts`);
    return res.data;
  },
  getAccount: async (id: number | string) => {
    const res = await axios.get(`${baseUrl}/accounts/${id}`);
    return res.data;
  },
  getUsers: async () => {
    const res = await axios.get(`${baseUrl}/accounts/users`);
    return res.data;
  },
  updateRole: async ({ email, role }: {email: string; role: string}) => {
    const res = await axios.post(`${baseUrl}/accounts/users`, { email, role });
    return res.data;
  },
  updateUserProfile: async (data: {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
  }) => {
    const res = await axios.put(`${baseUrl}/accounts/users/profile`, data);
    return res.data;
  },
  removeRole: async (email: string) => {
    // Pass as params so axios encodes it: a raw "+" in a plus-addressed email
    // would reach Rails as a space and the user would not be found.
    const res = await axios.delete(`${baseUrl}/accounts/users`, { params: { email } });
    return res.data;
  },
  listInvitations: async (): Promise<AccountInvitation[]> => {
    const res = await axios.get(`${accountUrl}/invitations`);
    return res.data;
  },
  // Also used to re-send: inviting the same email and role again renews the
  // pending invitation and emails it again.
  createInvitation: async (data: { email: string; role: InvitationRole }): Promise<AccountInvitation> => {
    const res = await axios.post(`${accountUrl}/invitations`, data);
    return res.data;
  },
  deleteInvitation: async (id: number) => {
    const res = await axios.delete(`${accountUrl}/invitations/${id}`);
    return res.data;
  },
  // Public: works signed out. When signed in, the auth headers let the API
  // answer "accept" for the invited user.
  getInvitation: async (key: string): Promise<InvitationDetails> => {
    const res = await axios.get(`${accountUrl}/invitations/${encodeURIComponent(key)}`);
    return res.data;
  },
  // Public. The response carries devise_token_auth headers, which the axios
  // response interceptor stores, so the new user is signed in afterwards.
  registerFromInvitation: async (key: string, data: InvitationRegistration) => {
    const res = await axios.post(`${accountUrl}/invitations/${encodeURIComponent(key)}/register`, data);
    return res.data;
  },
  // Active invitations addressed to the signed-in user's email. Keys are stored
  // only as digests, so these are accepted by id.
  myInvitations: async (): Promise<MyInvitation[]> => {
    const res = await axios.get(`${accountUrl}/invitations/mine`);
    return res.data;
  },
  acceptMyInvitation: async (id: number) => {
    const res = await axios.post(`${accountUrl}/invitations/mine/${id}/accept`);
    return res.data;
  },
  acceptInvitation: async (key: string) => {
    const res = await axios.post(`${accountUrl}/invitations/${encodeURIComponent(key)}/accept`);
    return res.data;
  },
};

export type InvitationRole = "payee" | "manage" | "production" | "owner";

export interface AccountInvitation {
  id: number;
  email: string;
  role: InvitationRole;
  created: string;
}

export type InvitationStatus = "register" | "login" | "accept" | "accepted" | "expired" | "missing";

export interface InvitationDetails {
  status: InvitationStatus;
  email?: string;
  role?: InvitationRole;
  account_name?: string;
}

export interface InvitationRegistration {
  first_name: string;
  last_name: string;
  phone: string;
  password: string;
  password_confirmation: string;
}

// owl before key digests (#1147) returns `key`; owl after (#1151) returns `id`.
// Keep both until #1151 is deployed everywhere, then drop `key`.
export interface MyInvitation {
  id?: number;
  key?: string;
  role: InvitationRole;
  account_id: number;
  account_name: string;
  created: string;
}
